import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  Grid,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Webhook as WebhookIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import api from '../../services/api';

interface WebhookData {
  webhookUrl: string;
  chave: string;
  criacao: string;
}

interface PixKey {
  id: string;
  type: string;
  key: string;
  status: string;
}

export default function PixWebhooksPage() {
  const [loading, setLoading] = useState(false);
  const [webhooks, setWebhooks] = useState<WebhookData[]>([]);
  const [pixKeys, setPixKeys] = useState<PixKey[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dialog states
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string>('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Receiver URL (your backend endpoint)
  const receiverUrl = `${window.location.origin}/api/v1/pix/webhooks/receiver`;

  const fetchWebhooks = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get('/pix/webhooks');

      // Handle both wrapped and unwrapped response formats
      const success = response?.success ?? response?.data?.success;
      const data = response?.data ?? response;

      if (success) {
        const webhooksData = data?.webhooks || data?.data || data || [];
        setWebhooks(Array.isArray(webhooksData) ? webhooksData : []);
      } else {
        setError(data?.error?.message || response?.error?.message || 'Erro ao carregar webhooks');
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.error?.message || err.message || 'Erro ao carregar webhooks');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPixKeys = useCallback(async () => {
    try {
      const response = await api.get('/pix/keys');
      const success = response?.success ?? response?.data?.success;
      const data = response?.data ?? response;

      if (success) {
        setPixKeys(data?.data || data || []);
      }
    } catch (err) {
      console.error('Error fetching PIX keys:', err);
    }
  }, []);

  useEffect(() => {
    fetchWebhooks();
    fetchPixKeys();
  }, [fetchWebhooks, fetchPixKeys]);

  const handleRegisterWebhook = async () => {
    if (!selectedKey || !webhookUrl) {
      setError('Selecione uma chave PIX e informe a URL do webhook');
      return;
    }

    setActionLoading(true);
    setError(null);

    try {
      const response = await api.put(`/pix/webhooks/${encodeURIComponent(selectedKey)}`, {
        webhookUrl,
      });

      const success = response?.success ?? response?.data?.success;

      if (success) {
        setSuccess('Webhook configurado com sucesso');
        setRegisterDialogOpen(false);
        setSelectedKey('');
        setWebhookUrl('');
        fetchWebhooks();
      } else {
        setError(response?.error?.message || response?.data?.error?.message || 'Erro ao configurar webhook');
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.error?.message || err.message || 'Erro ao configurar webhook');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteWebhook = async () => {
    if (!selectedKey) return;

    setActionLoading(true);
    setError(null);

    try {
      const response = await api.delete(`/pix/webhooks/${encodeURIComponent(selectedKey)}`);

      const success = response?.success ?? response?.data?.success;

      if (success) {
        setSuccess('Webhook removido com sucesso');
        setDeleteDialogOpen(false);
        setSelectedKey('');
        fetchWebhooks();
      } else {
        setError(response?.error?.message || response?.data?.error?.message || 'Erro ao remover webhook');
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.error?.message || err.message || 'Erro ao remover webhook');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    setSuccess('URL copiada para a área de transferência');
  };

  const openDeleteDialog = (chave: string) => {
    setSelectedKey(chave);
    setDeleteDialogOpen(true);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  // Get keys without webhook configured
  const keysWithoutWebhook = pixKeys.filter(
    key => !webhooks.some(wh => wh.chave === key.key)
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Webhooks PIX
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure webhooks para receber notificações de PIX recebidos em tempo real
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Atualizar">
            <IconButton onClick={fetchWebhooks} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setRegisterDialogOpen(true)}
            disabled={keysWithoutWebhook.length === 0}
          >
            Configurar Webhook
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Info Card */}
      <Card variant="outlined" sx={{ mb: 3, bgcolor: 'info.50' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <InfoIcon color="info" />
            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Como funciona o Webhook PIX?
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Ao configurar um webhook para uma chave PIX, o Sicoob enviará notificações
                automaticamente para a URL configurada sempre que um PIX for recebido nessa chave.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>URL do seu receptor:</strong>
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <TextField
                  size="small"
                  value={receiverUrl}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  sx={{ maxWidth: 500, fontFamily: 'monospace' }}
                />
                <Tooltip title="Copiar URL">
                  <IconButton onClick={() => handleCopyUrl(receiverUrl)} size="small">
                    <CopyIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Webhooks Table */}
      <Paper>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight={600}>
            <WebhookIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Webhooks Configurados ({webhooks.length})
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Chave PIX</TableCell>
                <TableCell>URL do Webhook</TableCell>
                <TableCell>Data de Criação</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : webhooks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Nenhum webhook configurado
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                webhooks.map((webhook, index) => (
                  <TableRow key={webhook.chave || index} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {webhook.chave}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            maxWidth: 300,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {webhook.webhookUrl}
                        </Typography>
                        <Tooltip title="Copiar URL">
                          <IconButton
                            size="small"
                            onClick={() => handleCopyUrl(webhook.webhookUrl)}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(webhook.criacao)}</TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label="Ativo"
                        color="success"
                        icon={<CheckIcon />}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Remover webhook">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => openDeleteDialog(webhook.chave)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Available PIX Keys */}
      {keysWithoutWebhook.length > 0 && (
        <Paper sx={{ mt: 3 }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Chaves PIX Disponíveis ({keysWithoutWebhook.length})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Chaves que ainda não possuem webhook configurado
            </Typography>
          </Box>
          <Divider />
          <Box sx={{ p: 2 }}>
            <Grid container spacing={2}>
              {keysWithoutWebhook.map((key) => (
                <Grid item xs={12} sm={6} md={4} key={key.id}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {key.type}
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {key.key}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Paper>
      )}

      {/* Register Dialog */}
      <Dialog open={registerDialogOpen} onClose={() => setRegisterDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Configurar Webhook PIX</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              select
              fullWidth
              label="Chave PIX"
              value={selectedKey}
              onChange={(e) => setSelectedKey(e.target.value)}
              SelectProps={{ native: true }}
              sx={{ mb: 3 }}
            >
              <option value="">Selecione uma chave...</option>
              {keysWithoutWebhook.map((key) => (
                <option key={key.id} value={key.key}>
                  {key.type}: {key.key}
                </option>
              ))}
            </TextField>

            <TextField
              fullWidth
              label="URL do Webhook"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://seu-dominio.com/api/webhook/pix"
              helperText="URL que receberá as notificações de PIX. Use HTTPS."
            />

            <Alert severity="info" sx={{ mt: 2 }}>
              Para receber notificações neste sistema, use a URL:
              <Box sx={{ mt: 1, fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all' }}>
                {receiverUrl}
              </Box>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegisterDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleRegisterWebhook}
            disabled={actionLoading || !selectedKey || !webhookUrl}
            startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            Configurar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Remover Webhook</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja remover o webhook da chave PIX?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontFamily: 'monospace' }}>
            {selectedKey}
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Ao remover, você deixará de receber notificações automáticas de PIX recebidos nessa chave.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteWebhook}
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          >
            Remover
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
